/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import trackGTMEvents from 'shared-utils/src/trackGTMEvents/new';
import sleep from 'shared-utils/src/sleep';

import Draw from 'shared-vectors/icons/MapDraw';
import Around from 'shared-vectors/icons/MapAround';
import Address from 'shared-vectors/icons/MapAddress';
import MagnifyingGlass from 'shared-vectors/icons/MagnifyingGlass';
import { Icon } from 'shared-vectors';

import GoogleSearch from '../../map/GoogleSearch';

import Pill from '../Pill';

import { DRAWING_ACTIONS_MAP } from '../../../constants';

import './style.scss';

const Toolbar = ({
  map,
  searchMode,
  setSearchMode,
  mobileOs,
  device,
  places,
  core,
  geocoding,
  setPinpoint,
  isQBuilder,
  gSearchOpen,
  toggleGSearchState,
}) => {
  const gSearchTrigger = useRef(null);
  const [isGSearchOn, setIsGSearchOn] = useState(false);
  const [gSearchTriggerAsBtn, setGSearchTriggerAsBtn] = useState(false);

  const updateGSearchState = (val) => {
    setIsGSearchOn(val);
    toggleGSearchState && toggleGSearchState(val);
  };

  useEffect(() => {
    if (!isQBuilder && isGSearchOn && !gSearchOpen) {
      setIsGSearchOn(gSearchOpen);
    }
  }, [gSearchOpen, isGSearchOn, isQBuilder]);

  useEffect(() => {
    if (searchMode === 'draw' && !gSearchTriggerAsBtn) {
      setGSearchTriggerAsBtn('button');
    } else if (!searchMode && gSearchTriggerAsBtn) {
      setGSearchTriggerAsBtn('invertedTransition');
      setTimeout(() => {
        setGSearchTriggerAsBtn('');
      }, 301);
    } else if (!searchMode && isGSearchOn) {
      updateGSearchState(false);
    }
  }, [searchMode, gSearchTriggerAsBtn]);
  
  const toggleOverlayer = action => setSearchMode(action);

  const actions = {
    draw: 'geopolygon',
    around: 'geocircle',
    nearby: 'nearby',
  };

  const startPolygon = () => {
    toggleOverlayer(DRAWING_ACTIONS_MAP.polygon);
    sleep(300).then(() => {
      trackGTMEvents({
        category: 'Interaction',
        action: 'TapSearchButton',
        label: actions[DRAWING_ACTIONS_MAP.polygon],
        position: 'SRPMap_Buttons',
      }, {}, true);
    });
  };
  const startCircle = () => {
    toggleOverlayer(DRAWING_ACTIONS_MAP.circle);
    sleep(300).then(() => {
      trackGTMEvents({
        category: 'Interaction',
        action: 'TapSearchButton',
        label: actions[DRAWING_ACTIONS_MAP.circle],
        position: 'SRPMap_Buttons',
      }, {}, true);
    });
  };
  const startNearby = () => {
    toggleOverlayer(DRAWING_ACTIONS_MAP.nearby);
    sleep(300).then(() => {
      trackGTMEvents({
        category: 'Interaction',
        action: 'TapSearchButton',
        label: actions[DRAWING_ACTIONS_MAP.nearby],
        position: 'SRPMap_Buttons',
      }, {}, true);
    });
  };
  
  const activateGoogleSearch = () => {
    updateGSearchState(true);
    sleep(300).then(() => trackGTMEvents({
      category: 'Interaction',
      action: 'SearchAddress',
    }));    
  };

  const gSearchAction = ({
    name,
    parent,
    location,
    viewport,
    favorite,
    address,
    number,
    town,
  }) => {
    const { LatLng, LatLngBounds } = core;

    const { lat, lng } = location;

    setPinpoint({
      name,
      parent,
      uKey: `pin_${lat},${lng}`,
      isPin: true,
      lat,
      lon: lng,
      favorite,
      address,
      number,
      town,
      fromDrawTool: searchMode === 'draw',
    });

    if (viewport) {
      const {
        north,
        east,
        south,
        west,
      } = viewport;
  
      const sboxbounds = new LatLngBounds();
      sboxbounds.extend(new LatLng({ lat: north, lng: east }));
      sboxbounds.extend(new LatLng({ lat: south, lng: west }));
  
      map.fitBounds(sboxbounds);
    } else {
      const center = new LatLng({ lat, lng });
      map.setCenter(center);
      map.setZoom(18);
    }
  };

  const showBasedOnSearchMode = !searchMode || searchMode === 'draw';
  const isShrinked = searchMode === 'draw' && device === 'smartphone';

  if (map && showBasedOnSearchMode) {
    return (
      <>
        <div
          ref={gSearchTrigger}
          className={`csa_mapsearch__bod__map__gsearchtrigger p--s tp-s--l tp-w--s c-txt--f5 c-bg--w is-clickable ${gSearchTriggerAsBtn}`}
          onClick={activateGoogleSearch}
        >
          <Icon
            name={MagnifyingGlass}
            width="24px"
            className="mr--xs"
            fill
          />
          <span>Cerca indirizzo</span>
        </div>
        {isGSearchOn ? (
          <GoogleSearch
            map={map}
            mobileOs={mobileOs}
            device={device}
            places={places}
            core={core}
            geocoding={geocoding}
            setPinpoint={setPinpoint}
            searchTrigger={gSearchTrigger.current}
            isSearching={isGSearchOn}
            setIsSearching={updateGSearchState}
            className="csa_mapsearch__bod__map__gsearch"
            action={gSearchAction}
            geolocationTrackPosition="SearchAddress"
            isQBuilder={isQBuilder}
          />
        ) : null}
        <div className={`csa_mapsearch__bod__map__btns${isShrinked ? ' shrinked' : ''}`}>
          <div className="csa_mapsearch__bod__map__btns__lens mr--s c-bg--w" onClick={activateGoogleSearch}>
            <Icon name={MagnifyingGlass} width="24px" fill />
          </div>
          {!searchMode ? (
            <>
              <Pill
                action={startPolygon}
                leftIcon={<Icon name={Draw} className="mr--xs" width="24px" fill />}
                hasShadow
              >
                Disegna
              </Pill>
              <Pill
                action={startCircle}
                className="csa_mapsearch__bod__map__btns__ml"
                leftIcon={<Icon name={Around} className="mr--xs" width="24px" fill />}
                hasShadow
              >
                Intorno a
              </Pill>
              <Pill
                action={startNearby}
                className="csa_mapsearch__bod__map__btns__ml"
                leftIcon={<Icon name={Address} className="mr--xs" width="24px" fill />}
                hasShadow
              >
                A pochi minuti da
              </Pill>
            </>
          ) : null}
          
        </div>
      </>
    );
  }

  return null;
};

export default Toolbar;

Toolbar.propTypes = {
  map: PropTypes.instanceOf(Object),
  searchMode: PropTypes.string,
  setSearchMode: PropTypes.func,
  mobileOs: PropTypes.string,
  device: PropTypes.string,
  places: PropTypes.instanceOf(Object),
  core: PropTypes.instanceOf(Object),
  geocoding: PropTypes.instanceOf(Object),
  setPinpoint: PropTypes.func,
  isQBuilder: PropTypes.bool,
  hasBackButtonHistory: PropTypes.bool,
};

Toolbar.defaultProps = {
  map: null,
  searchMode: null,
  setSearchMode: null,
  mobileOs: '',
  device: '',
  places: null,
  core: null,
  geocoding: null,
  setPinpoint: null,
  isQBuilder: false,
  hasBackButtonHistory: false,
};
